import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

type UseTabs<T,> = {
	defaultSelected: T,
	selected?: T,
	param?: string,
	disableParams?: boolean,
}

const useTabs = <T extends string | number | Record<string, string>,>({
	defaultSelected,
	selected,
	param = "tab",
	disableParams,
}: UseTabs<T extends Record<string, string> ? T[keyof T] : T>) => {

	const [ searchParams, setSearchParams ] = useSearchParams();
	const currentParam = disableParams ? null : searchParams.get(param);

	const [ selectedTab, selectTab ] = useState(() => {
		if (!currentParam) {
			return defaultSelected;
		}

		if (defaultSelected.toString() === defaultSelected.toString().toUpperCase()) {
			return currentParam.toUpperCase() as T;
		} else if (defaultSelected.toString() === defaultSelected.toString().toLowerCase()) {
			return currentParam.toLowerCase() as T;
		}
		return currentParam as T;
	});

	useEffect(() => {
		if (!disableParams) {
			setSearchParams((prev) => {
				const nextParams = new URLSearchParams(prev);
				nextParams.set(param, String(selectedTab));
				return nextParams;
			}, { replace: true });
		}
	}, [ selectedTab ]);

	useEffect(() => {
		if (selected && selected !== selectedTab) {
			selectTab(selected);
		}
	}, [ selected ]);

	return {
		selectedTab,
		selectTab: selectTab,
	};
};

export default useTabs;
